import { Inject, Injectable, Optional } from '@angular/core';
import { setCurrentRouteIdentifier, setLastRecordedNavigationStartMarker } from '@office-start/performance-service';
import { logError, logPerformanceWithParams } from '@office-start/telemetry-service';
import { filter, take } from 'rxjs/operators';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FileType } from '../models/file-item.model';
import { ObservableScope } from '../models/observable-scope.enum';
import { AngularEvents } from '../shared-context/angular-events';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ScopableObservable, ScopableSubject } from '../utilities/scopable-subject.utility';
import { DataSecurityService } from './data-security.service';
import { EventService } from './event.service';
import { OfficeRouteItem, OfficeRouteService } from './office-route.service';
import { PERFORMANCE_QUEUE } from './performance-queue.injection-token';
import { UnloadEventService } from './unload-event.service';
import { WindowService } from './window.service';

// These items are copied and adapted in shared-inline.ts
// TODO: Refactor to allow performance code to be shared between inline and app bundles
// Start of copied content
type PerformanceDumpEntry = { name: string, duration: number, params?: {} };

export const enum KnownPerformanceMarks {
    AppBeginBootstrap = 'App_Begin_Bootstrap',
    AppCompleteBootstrap = 'App_Complete_Bootstrap',
    AppInitializationComplete = 'App_Initialization_Complete',
    AppRouteNavigationStart = 'App_Navigation_Start',
    AppUnloaded = 'App_Unloaded',
    ConnectEnd = 'connectEnd',
    ConnectStart = 'connectStart',
    DomainLookupEnd = 'domainLookupEnd',
    DomainLookupStart = 'domainLookupStart',
    DomComplete = 'domComplete',
    DomInteractive = 'domInteractive',
    DomLoading = 'domLoading',
    FetchStart = 'fetchStart',
    NavigationStart = 'navigationStart',
    RequestStart = 'requestStart',
    ResponseEnd = 'responseEnd',
    ResponseStart = 'responseStart',
    UnloadEventEnd = 'unloadEventEnd',
    UnloadEventStart = 'unloadEventStart',
    FirstRouteChange = 'firstRouteChange',
    SecondRouteChange = 'secondRouteChange'
}
// End of copied content

export class Performance {
    private static nowOffset : number;

    public static now(): number {
        const performance: any = window.performance;
        if (!!(performance && performance.now)) {
            return performance.now();
        } else {
            if (typeof this.nowOffset === 'undefined') {
                const accurateTimingAvailable = !!(performance && performance.timing && performance.timing.navigationStart);
                this.nowOffset = accurateTimingAvailable ? performance.timing.navigationStart : Date.now();
            }
            return Date.now() - this.nowOffset;
        }
    }
}

export const enum PerformanceMeasureType {
    NoMeasure,
    FromBrowserNavigationStart,
    FromLastAppNavigationStart
}

@Injectable({
    providedIn: 'root'
})
export class PerformanceService {

    private readonly loggingEnabled: boolean;
    private readonly dumpedMeasures = new Set<string>();
    private dumpedBrowserMarkers = false;
    private readonly recordedMarkers = new Map<string, number>();
    private readonly recordedMeasures = new Map<string, string>();
    private readonly paramsForAppMeasures = new Map<string, InstrumentationParams>();
    private globalStateParamsForBrowserMeasures: InstrumentationParams = null;
    private currentRoute: OfficeRouteItem;
    private lastRecordedRouteNavigationStartMarker: string;
    private readonly _marks = new ScopableSubject<PerformanceMark>();
    private static numberOfRouteChanges = 0;

    constructor(
        @Optional() @Inject(PERFORMANCE_QUEUE) private readonly queuedMarks: Array<QueuedAppMark>,
        private readonly dataSecurityService: DataSecurityService,
        eventService: EventService,
        unloadEventService: UnloadEventService,
        private readonly instrumentationService: InstrumentationService,
        private readonly windowService: WindowService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        routeService: OfficeRouteService
    ) {
        // All newer browsers support the API's needed. Disable perf logging for
        // older browsers as the data is inaccurate and skews our metrics
        this.loggingEnabled = PerformanceService.performanceLoggingSupported(this.windowService.nativeWindow);
        if (!this.loggingEnabled) {
            return;
        }

        // Do initial dump once Shell is loaded
        eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.shellPlusLoaded),
            take(1))
            .toPromise()
            .then(() => {
                // Log performance dump with 1 second delay after Shell is loaded
                setTimeout(() => this.log(), 1000);
            });

        // Use page unload event as last resort for logging all recorded perf markers
        unloadEventService.addUnloadEvent(() => {
            this.mark(KnownPerformanceMarks.AppUnloaded, PerformanceMeasureType.FromBrowserNavigationStart);
            this.log();
        });

        const recordRouteNavigationStartMarker = (routeItem: OfficeRouteItem) => {
            // Update reference to current route
            this.currentRoute = routeItem;
            // Log only first occurrence of appRouteNavigationStart marker; the rest occurrences will
            // happen at arbitrary point in time and less interesting from perf analysis perspective
            // We measure from responseStart in officehome or bootstrap event in pwa
            this.mark(
                KnownPerformanceMarks.AppRouteNavigationStart,
                this.lastRecordedRouteNavigationStartMarker ? PerformanceMeasureType.NoMeasure : (this.windowService.nativeWindow.measureAppFrom || KnownPerformanceMarks.ResponseStart));
        };

        // Handle navigation changes in the app and log corresponding markers
        routeService.subscribeToRouteChangeStart(routes => {
            if (routes.currentRoute && routes.currentRoute.route) {
                recordRouteNavigationStartMarker(routes.currentRoute);
            }
        });
        routeService.subscribeToRouteChangeEnd(routes => {
            // It is possible for route change start to already complete by the time Performance Service is instantiated.
            // If this is the case then record navigation start marker post-factum.
            if (routes.currentRoute && routes.currentRoute.route && !this.currentRoute) {
                recordRouteNavigationStartMarker(routes.currentRoute);
            }

            // Record global state values for all browser markers when first app navigation happens
            if (this.globalStateParamsForBrowserMeasures === null) {
                this.globalStateParamsForBrowserMeasures = {};
                this.instrumentationService.applyGlobalStateParams(this.globalStateParamsForBrowserMeasures, true);
            }

            this.trackRouteChange();

        });

        this.drainQueuedMarks();
    }

    // Below code is copied and adapted in performance.service.ts
    // TODO: Refactor to allow code sharing between inline files and angular app
    // Start of copied content
    private static readonly globalAllowedMarkers = new Map<string, string>([
        [KnownPerformanceMarks.FetchStart, KnownPerformanceMarks.NavigationStart],
        [KnownPerformanceMarks.UnloadEventStart, KnownPerformanceMarks.NavigationStart],
        [KnownPerformanceMarks.UnloadEventEnd, KnownPerformanceMarks.UnloadEventStart],
        [KnownPerformanceMarks.DomainLookupEnd, KnownPerformanceMarks.DomainLookupStart],
        [KnownPerformanceMarks.ConnectEnd, KnownPerformanceMarks.ConnectStart],
        [KnownPerformanceMarks.RequestStart, KnownPerformanceMarks.NavigationStart],
        [KnownPerformanceMarks.ResponseStart, KnownPerformanceMarks.RequestStart],
        [KnownPerformanceMarks.ResponseEnd, KnownPerformanceMarks.ResponseStart],
        [KnownPerformanceMarks.DomLoading, KnownPerformanceMarks.ResponseStart],
        [KnownPerformanceMarks.DomInteractive, KnownPerformanceMarks.DomLoading],
        [KnownPerformanceMarks.DomComplete, KnownPerformanceMarks.DomLoading]
    ]);

    public static performanceLoggingSupported(nativeWindow: Window): boolean {
        const performance = nativeWindow.performance;
        return !!(performance && performance.now && performance.mark && performance.timing && performance.timing.navigationStart && performance.measure && performance.getEntriesByType);
    }

    public static getBrowserTimingMeasures(nativeWindow: Window, handleError: (errorName: string, errorMsg: string) => void, commonParams?: InstrumentationParams, onMeasureAdded?: (marker: string, measure: string) => void): Array<PerformanceDumpEntry> {
        const dump = new Array<PerformanceDumpEntry>();

        if (!PerformanceService.performanceLoggingSupported(nativeWindow)) {
            return dump;
        }

        // Add all the browser timing info (see http://www.w3.org/TR/navigation-timing/#processing-model)
        // ReSharper disable once MissingHasOwnPropertyInForeach
        for (const marker in nativeWindow.performance.timing) {
            // Limit only to browser markers that we are interested in
            const relativeMarker = PerformanceService.globalAllowedMarkers.get(marker);
            if (!relativeMarker) {
                continue;
            }

            const value = nativeWindow.performance.timing[marker];
            const relativeValue = nativeWindow.performance.timing[relativeMarker];

            // Do not record measure when value for marker and/or relative marker is not a number, indicating some sort of bad state of performance.timing
            if (typeof value !== 'number' || typeof relativeValue !== 'number') {
                handleError(marker, `Failed to calculate measure for <${marker}> starting from <${relativeMarker}> due to unexpected value type: <${marker}> value type is ${typeof value}; <${relativeMarker}> value type is ${typeof relativeValue}`);
                continue;
            }

            // Do not record measure when value for marker and/or relative marker is '0', indicating that corresponding moment never happened
            if (value === 0 || relativeValue === 0) {
                // Unload markers are expected to have zero value when it was fresh page load or unloaded page was from different domain
                if (marker !== KnownPerformanceMarks.UnloadEventStart && marker !== KnownPerformanceMarks.UnloadEventEnd) {
                    handleError(marker, `Failed to calculate measure for <${marker}> starting from <${relativeMarker}> due to unexpected zero values: <${marker}>=${value}; <${relativeMarker}>=${relativeValue}`);
                }

                continue;
            }

            // Marker value should not be less than the one recorded for relative marker
            if (value < relativeValue) {
                handleError(marker, `Failed to calculate measure for <${marker}> starting from <${relativeMarker}> due to relative marker value being greater than marker value: <${marker}>=${value}; diff from <${relativeMarker}>=${value - relativeValue}`);
                continue;
            }

            // All marker values must be greater or equal to navigationStart
            if (value < nativeWindow.performance.timing.navigationStart) {
                handleError(marker, `Failed to calculate measure for <${marker}> starting from <${relativeMarker}> due to marker value being less than <navigationStart> marker value: <${marker}>=${value}; diff from <navigationStart}>=${value - nativeWindow.performance.timing.navigationStart}`);
                continue;
            }

            if (relativeValue < nativeWindow.performance.timing.navigationStart) {
                handleError(marker, `Failed to calculate measure for <${marker}> starting from <${relativeMarker}> due to relative marker value being less than <navigationStart> marker value: <${marker}>=${value}; <${relativeMarker}>=${relativeValue}; diff from <navigationStart}>=${relativeValue - nativeWindow.performance.timing.navigationStart}`);
                continue;
            }

            const duration = Math.ceil(value - relativeValue);
            dump.push({ name: marker, duration, params: !commonParams ? {} : { ...commonParams } });

            // In older versions of Firefox (<= 52) if the measure name provided to
            // performance.measure() is a known browser marker name (like 'navigationStart' etc),
            // a "illegal string" error is thrown. This suffix is added to all measure names to avoid
            // this exception. performance_dump entries will not have this prefix, but it will show up
            // in the browser's performance measure viewer.
            const measureName = `${marker}_ohp_measure`;

            if (onMeasureAdded) {
                onMeasureAdded(marker, measureName);
            }

            // Record measure so it shows up in Dev tools
            // BUGBUG: The following call may fail on older version of Firefox browsers when Firefox deem start and/or marker values to be invalid.
            // Wrapping into try/catch so we have direct way of checking how often it happens. Consider removing try/catch in case this is no longer happening.
            try {
                nativeWindow.performance.measure(measureName, relativeMarker, marker);
            } catch (ex) {
                handleError(marker, `Failed to record measure for <${marker}> starting from <${relativeMarker}>: exception is thrown`);
            }
        }

        return dump;
    }
    // End of copied content

    /**
     * Generates performance marker and generates measure with the same name which is calculated from given relative point.
     * The measure will be logged automatically unless PerformanceMeasureType.NoMeasure is specified. Marker and measure
     * names will be appended with current app route identifier and (for repeat marker) index corresponding for the number
     * of occurrences. On the other hand, event logged for calculated measure will have the canonical name passed to this
     * method. This is due to the fact that logged events carry additional information in separate Page property.
     * Also note that event will be logged only for first marker occurrence as subsequent markers are rarely interesting
     * from perf analysis perspective.
     * @param name Mark name. Before recording given name will be appended with current route and app name (if specified for the route).
     * For instance, if current route is 'Start' and app is 'Word' and mark name is 'Mru_Ready' then recorded name will be 'Mru_Ready_StartWord'.
     * If same mark name is produced multiple times then name will be automatically appended with index that corresponds to the count of
     * previous appearences of the same mark. For instance, when mark('Some_Mark') is called for first time then it will be recorded as
     * 'Some_Mark'; when mark('Some_Mark') is called for second time then it will be recorded as 'Some_Mark.2'; when mark('Some_Mark') is
     * called for third time then it will be recorded as 'Some_Mark.3' and so on.
     * @param measureFrom Point from which to measure duration that will be associated with the mark. By default it is set to
     * PerformanceMeasureType.FromLastAppNavigationStart which corresponds to the last navigation inside app. It can also be set to
     * PerformanceMeasureType.FromBrowserNavigationStart which corresponds to the time when user navigated to office.com in browser.
     * If measureFrom is string then it represents specific mark that was recorded beforehand.
     * Finally, PerformanceMeasureType.NoMeasure is used to specify that measure should not be recorded and logged.
     * @param securityThreshold Lowest security level at which this mark will be recorded. Omit to always allow.
     * @param params Optional parameters to include when measure is logged.
     * @returns Marker that was added to browser performance markers. Can be passed to future mark() calls as measureFrom argument.
     */
    public mark(name: string, measureFrom: PerformanceMeasureType | string = PerformanceMeasureType.FromLastAppNavigationStart, securityThreshold: DataSecurityLevel = DataSecurityLevel.Security, params?: InstrumentationParams): string {
        if (!this.loggingEnabled) {
            return;
        }

        // NOTE: We will not apply marks to browser performance object if the windows security settings do not allow it
        // To observe all marks ensure your Settings->Privacy->Diagnostics&Feedback is set to 'Enhanced' (MSIT doens't allow 'Full')
        if (securityThreshold > this.dataSecurityService.securityLevel) {
            return;
        }

        // Append mark name with route identifier so it is easy to distinguish them if same mark is logged on different route
        let nameWithRouteQualifier = name;
        if (this.currentRoute && this.currentRoute.route) {
            const routeIdentifier = `${this.currentRoute.route}${this.currentRoute.app ? FileType[this.currentRoute.app] : ''}`;
            setCurrentRouteIdentifier(routeIdentifier);
            nameWithRouteQualifier = `${name}_${routeIdentifier}`;
        }

        // Append count number if marker was already recorded before
        let marker = nameWithRouteQualifier;
        if (this.recordedMarkers.has(marker)) {
            const count = this.recordedMarkers.get(marker) + 1;
            this.recordedMarkers.set(marker, count);
            marker = `${nameWithRouteQualifier}.${count}`;
        } else {
            this.recordedMarkers.set(marker, 1);
        }

        this.windowService.nativeWindow.performance.mark(marker);

        // Update last navigation start marker
        const lastNavigationStartMarker = this.lastRecordedRouteNavigationStartMarker || KnownPerformanceMarks.NavigationStart;
        if (name === KnownPerformanceMarks.AppRouteNavigationStart) {
            setLastRecordedNavigationStartMarker(marker);
            this.lastRecordedRouteNavigationStartMarker = marker;
        }

        // Calculate timing info from specified relative point
        let startMark: string = KnownPerformanceMarks.NavigationStart;
        if (measureFrom === PerformanceMeasureType.FromLastAppNavigationStart) {
            startMark = lastNavigationStartMarker;
        } else if (measureFrom === PerformanceMeasureType.NoMeasure) {
            startMark = '';
        } else if (typeof measureFrom === 'string') {
            startMark = this.getRecordedMarkerNameBySubstring(measureFrom) ?? measureFrom;
        }

        this.addMeasure(nameWithRouteQualifier, marker, startMark, name, params);
        // Drain queued markers in case any were added after service creation
        this.drainQueuedMarks();
        return marker;
    }

    public getRecordedMarkerNameBySubstring(substring: string): string {
        if (!substring) {
            return null;
        }

        return Array.from(this.recordedMarkers.keys()).find(marker => marker.indexOf(substring) >= 0);
    }

    public getResourceTimingEntries(url: string): Array<PerformanceResourceTiming & { workerStart: number }> {
        try {
            const resourceTimingEntries = this.windowService.nativeWindow.performance
                .getEntriesByType('resource')
                .filter(entry => entry.name.lastIndexOf(url) >= 0);

            return resourceTimingEntries as Array<PerformanceResourceTiming>;
        } catch (ex) {
            return [];
        }
    }

    public get marks(): ScopableObservable<PerformanceMark> {
        return this._marks.asScopableObservable();
    }

    public get navigationStart(): number {
        return this.getPerformanceTimingMember(KnownPerformanceMarks.NavigationStart);
    }
    public get fetchStart(): number {
        return this.getPerformanceTimingMember(KnownPerformanceMarks.FetchStart);
    }

    private trackRouteChange() {
        switch (PerformanceService.numberOfRouteChanges) {
            case 0:
                this.mark(KnownPerformanceMarks.FirstRouteChange);
                PerformanceService.numberOfRouteChanges++;
                break;
            case 1:
                this.mark(KnownPerformanceMarks.SecondRouteChange, KnownPerformanceMarks.FirstRouteChange);
                PerformanceService.numberOfRouteChanges++;
                break;
            default:
                break;
        }
    }

    private getPerformanceTimingMember(attribute: string) {
        const performance = this.windowService.nativeWindow.performance;
        if (!!(performance && performance.timing && performance.timing[attribute])) {
            return performance.timing[attribute];
        }
        return 0;
    }

    private drainQueuedMarks() {
        if (this.queuedMarks) {
            this.queuedMarks.splice(0, this.queuedMarks.length).forEach( mark => this.addMeasure(mark.markName, mark.markName, mark.measureFrom || KnownPerformanceMarks.NavigationStart, mark.markName, mark.eventParams));
        }
    }

    private addMeasure(measureName: string, measureTo: string, measureFrom: string, loggedName?: string, params?: InstrumentationParams) {
        const nativeWindow = this.windowService.nativeWindow;
        const recordedPerformanceMark: PerformanceMark = nativeWindow.performance.getEntriesByType('mark').find(entry => entry.name === measureTo);
        if (recordedPerformanceMark) {
            this._marks.next(recordedPerformanceMark);
        }

        // Exit early if logging not requested for given mark
        if (!measureFrom) {
            return;
        }

        // Do not record duplicate measures.
        // IMPORTANT: Note that measure name includes route qualifier but does not include repeat counter.
        // This means that measure will be recorded only once and repeat markers will not have measures.
        // This is done intentionally since measures are logged and we only interested when something
        // happens for the first time. This eliminates boiler plate code that we would have to put in all
        // places where we call performanceService.mark() to record measure only when mark is recorded for
        // the first time.
        if (this.recordedMeasures.has(measureName)) {
            return;
        }

        // Preserve original name that will be used when dumping recorded measures for logging purposes
        this.recordedMeasures.set(measureName, loggedName || measureName);

        // BUGBUG: The following call may throw exception in case startMark value is deemed to be invalid. This seem to
        // primarily happen on Edge/IE and Firefox. Wrapping into try/catch so we have direct way of checking how often it happens.
        // Consider removing try/catch in case this is no longer happening.
        try {
            nativeWindow.performance.measure(measureName, measureFrom, measureTo);
        } catch (ex) {
            this.logError(measureName, `Failed to record <${measureName}> for <${measureTo}> starting from <${measureFrom}>: exception is thrown`);
            return;
        }

        // If all Browser performance timing markers have been already dumped then log measure as soon as it comes
        if (this.dumpedBrowserMarkers) {
            if (params) {
                this.paramsForAppMeasures.set(measureName, params);
            }
            this.log(measureName);
        } else {
            // Measure will be logged later together with browser markers; preserve global state values for such
            // measure so they are accurately reflected in logged data
            params = params || {};
            this.instrumentationService.applyGlobalStateParams(params, false);
            this.paramsForAppMeasures.set(measureName, params);
        }
    }

    private log(name?: string): void {
        const dump = this.getPerformanceDump(name);
        dump.forEach(value => {
            this.instrumentationService.logPerformanceEvent(value.name, value.duration, value.params);
            // TODO: Log additional params to Diagnostic event after the new API is ready
            logPerformanceWithParams({eventName: value.name, latency: value.duration, instrumentationParams: value.params });
        });
    }

    private getPerformanceDump(specificMeasure?: string): Map<string, PerformanceDumpEntry> {
        const dump = new Map<string, PerformanceDumpEntry>();

        // Check if there is anything to do
        if (!specificMeasure && this.dumpedBrowserMarkers) {
            return dump;
        }

        const nativeWindow = this.windowService.nativeWindow;

        let appMeasures: Array<RawTimingEntry>;
        if (specificMeasure) {
            // Get specific measure if requested
            const rawMeasure = nativeWindow.performance.getEntriesByName(specificMeasure, 'measure');
            if (!rawMeasure) {
                return dump;
            }
            appMeasures = rawMeasure;
        } else {
            // Get all recorded measures that have not been dumped yet
            appMeasures = nativeWindow.performance
                .getEntriesByType('measure')
                .filter(measure => !this.dumpedMeasures.has(measure.name) && this.recordedMeasures.has(measure.name));
        }

        appMeasures.forEach(timingEntry => {
            const duration = Math.ceil(timingEntry.duration);

            let params: InstrumentationParams = {};
            if (this.paramsForAppMeasures.has(timingEntry.name)) {
                params = this.paramsForAppMeasures.get(timingEntry.name);
            }

            // Use original marker name without route qualifier as entry name: route qualifier is logged in Page property
            // which is global property, it is logged for all events
            const measureWithoutRouteQualifier = this.recordedMeasures.get(timingEntry.name);
            dump.set(timingEntry.name, { name: measureWithoutRouteQualifier, duration, params });

            this.dumpedMeasures.add(timingEntry.name);
        });

        if (!specificMeasure) {
            this.dumpBrowserMarkers(dump);
        }

        return dump;
    }

    private dumpBrowserMarkers(dump: Map<string, PerformanceDumpEntry>) {
        if (this.dumpedBrowserMarkers) {
            return;
        }

        const nativeWindow = this.windowService.nativeWindow;

        // Measure time for CDN asset which takes the longest to download
        if (this.clientStartupConfigService.config.resourceLoggingEnabled && this.clientStartupConfigService.config.cdnDomain) {
            const cdnDomain = this.clientStartupConfigService.config.cdnDomain;
            // This regex will make sure we measure the files served from our OfficeHome CDN.
            // initiatorType === 'script' makes sure we get from <script> elements
            // TODO: measure shell download latency as well
            const cdnRegex = new RegExp(`https://${cdnDomain}.*`, 'i');
            const resourceTimingEntries = nativeWindow.performance
                .getEntriesByType('resource')
                .filter((entry: PerformanceResourceTiming) => cdnRegex.test(entry.name) && (entry.initiatorType === 'script' || entry.initiatorType === 'link'))
                .filter((entry: PerformanceResourceTiming) => {
                    // Cached assets will have transferSize of 0.
                    // However, the transferSize property is not widely supported yet, so instead use a heuristic
                    // to determine if asset is cached if transferSize is not available.
                    if (entry.hasOwnProperty('transferSize')) {
                        return entry.transferSize > 0;
                    } else {
                        const transferTimeMs = entry.responseEnd - entry.responseStart;
                        return transferTimeMs > 20;
                    }
                });

            // Identify the resource from CDN that takes the longest to complete 'responseEnd'
            let maxResponseEndResourceTimingEntry;
            let max = 0;
            for (const resourceTimingEntry of resourceTimingEntries) {
                // These are the markers we want for each resource
                const timingEntry = resourceTimingEntry[KnownPerformanceMarks.ResponseEnd];
                if (typeof timingEntry === 'number' && timingEntry > 0) {
                    if (timingEntry > max) {
                        max = timingEntry;
                        maxResponseEndResourceTimingEntry = resourceTimingEntry;
                    }
                }
            }

            if (maxResponseEndResourceTimingEntry) {
                const filename: string = maxResponseEndResourceTimingEntry.name.split('/').pop();
                const marker = 'Last_CDN_Script_Downloaded';
                const responseEnd: number = maxResponseEndResourceTimingEntry.responseEnd;
                const params: InstrumentationParams = this.globalStateParamsForBrowserMeasures === null ? {} : { ...this.globalStateParamsForBrowserMeasures };
                params[InstrumentationParam.Filename] = filename;

                dump.set(marker, { name: marker, duration: responseEnd, params });

                this.dumpedMeasures.add(marker);
            }
        }

        const browserTimingMeasures = PerformanceService.getBrowserTimingMeasures(
            nativeWindow,
            (name, error) => {
                this.logError(name, error);
            },
            this.globalStateParamsForBrowserMeasures,
            (marker, measureName) => {
                this.dumpedMeasures.add(measureName);
                this.recordedMeasures.set(measureName, marker);
            }
        );

        browserTimingMeasures.forEach( measure => {
            dump.set(measure.name, measure);
        });

        this.dumpedBrowserMarkers = true;
    }

    private logError(error: string, debugInfo: string) {
        const params: InstrumentationParams = {
            [InstrumentationParam.Error]: error,
            [InstrumentationParam.DebugInfo]: debugInfo
        };
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PerformanceMarkFailure, params);
        logError({
            eventName: 'Performance_Mark_Failure',
            errorType: 'Other',
            message: error,
            debugInfo
        });
    }
}
